import * as THREE from 'three';
import { ParticleSystem } from './ParticleSystem';
import { config } from '@shared/config';
import { clientSettings } from '../ClientSettings';

// ============================================================
// GameEffects -- manages multiple particle systems for
// tire dust, boost flames, wall sparks, item pickup sparkles,
// and slipstream wind lines
// ============================================================

export class GameEffects {
  private dustSystem: ParticleSystem;
  private boostSystem: ParticleSystem;
  private sparkSystem: ParticleSystem;
  private pickupSystem: ParticleSystem;
  private slipstreamSystem: ParticleSystem;

  /** Accumulator for dust emission rate-limiting (emit a few particles per frame) */
  private dustAccum = 0;

  constructor(scene: THREE.Scene) {
    // ── Tire dust: small grey-brown puffs behind the kart ──
    this.dustSystem = new ParticleSystem(scene, {
      maxParticles: config.particles.dust.maxCount,
      lifetime: config.particles.dust.lifetime,
      speed: 2.0,
      spread: 0.4,
      size: 4.0,
      color: new THREE.Color(0.45, 0.38, 0.3),
      fadeOut: true,
      gravity: 1.5,
    });

    // ── Boost embers: fire-shaped flame particles trailing backward ──
    // HDR color (values > 1.0) ensures bloom picks up a hot orange halo.
    this.boostSystem = new ParticleSystem(scene, {
      maxParticles: config.particles.boost.maxCount,
      lifetime: config.particles.boost.lifetime,
      speed: 8.0,
      spread: 0.25,
      size: 4.0,
      color: new THREE.Color(2.0, 0.9, 0.2), // HDR yellow-orange
      fadeOut: true,
      gravity: 2.0,
      shape: 'fire',
    });

    // ── Wall sparks: bright white/yellow burst on collision ──
    this.sparkSystem = new ParticleSystem(scene, {
      maxParticles: config.particles.sparks.maxCount,
      lifetime: config.particles.sparks.lifetime,
      speed: 12.0,
      spread: 0.8,
      size: 3.5, // slightly larger for better visibility
      color: new THREE.Color(1.0, 0.95, 0.7),
      fadeOut: true,
      gravity: 14.0,
      shape: 'circle',
    });

    // ── Pickup sparkle: 4-point star burst when collecting an item box ──
    // HDR gold color (values > 1.0) for a strong bloom halo on pickup.
    this.pickupSystem = new ParticleSystem(scene, {
      maxParticles: config.particles.pickup.maxCount,
      lifetime: config.particles.pickup.lifetime,
      speed: 6.0,
      spread: 0.7,
      size: 5.0,
      color: new THREE.Color(2.0, 1.7, 0.4), // HDR gold
      fadeOut: true,
      gravity: 4.0,
      shape: 'star',
    });

    // ── Slipstream wind lines: blue-white elongated particles ──
    this.slipstreamSystem = new ParticleSystem(scene, {
      maxParticles: 80,
      lifetime: 0.8,
      speed: 10.0,
      spread: 0.15,
      size: 3.0,
      color: new THREE.Color(0.6, 0.8, 1.0),
      fadeOut: true,
      gravity: 0.5,
    });
  }

  // ────────────────────────────────────────────────────────────
  // Public emission methods
  // ────────────────────────────────────────────────────────────

  /**
   * Emit tire dust behind a driving kart. Call every frame while the kart moves.
   *
   * @param x   Kart world X
   * @param z   Kart world Z
   * @param yaw Kart yaw (radians, 0 = +X)
   * @param speed Kart speed (absolute)
   * @param dt  Frame delta time
   * @param offRoad Optional: true if the kart is off-road (brownish-red dust)
   */
  emitDust(x: number, z: number, yaw: number, speed: number, dt: number, offRoad = false): void {
    const pScale = clientSettings.getParticleScale();
    if (pScale <= 0) return;

    const absSpeed = Math.abs(speed);
    if (absSpeed < 3) return; // no dust at low speed

    // Rate scales with speed and particle density setting
    const rate = (20 + (absSpeed / 25) * 60) * pScale;
    this.dustAccum += rate * dt;

    const count = Math.floor(this.dustAccum);
    if (count <= 0) return;
    this.dustAccum -= count;

    // Temporarily change dust color for off-road
    if (offRoad) {
      // Brownish-red dust for off-road
      (this.dustSystem as any).config.color.setRGB(0.6, 0.3, 0.15);
    } else {
      // Default grey-brown
      (this.dustSystem as any).config.color.setRGB(0.45, 0.38, 0.3);
    }

    // "Behind" direction in world space (opposite of kart forward)
    const behindX = -Math.cos(yaw);
    const behindZ = -Math.sin(yaw);

    // Spawn from slightly behind and below the kart (two rear wheel positions)
    const rearOffsetX = behindX * 1.2;
    const rearOffsetZ = behindZ * 1.2;

    // Emit half from left wheel, half from right wheel
    const rightX = -Math.sin(yaw);
    const rightZ = Math.cos(yaw);
    const halfCount = Math.max(1, Math.floor(count / 2));

    // Left rear wheel
    this.dustSystem.emit(
      x + rearOffsetX - rightX * 0.7,
      0.1,
      z + rearOffsetZ - rightZ * 0.7,
      behindX, 0.3, behindZ,
      halfCount
    );

    // Right rear wheel
    this.dustSystem.emit(
      x + rearOffsetX + rightX * 0.7,
      0.1,
      z + rearOffsetZ + rightZ * 0.7,
      behindX, 0.3, behindZ,
      count - halfCount
    );
  }

  /**
   * Emit boost flame particles behind a boosting kart. Call every frame while boosting.
   *
   * @param x   Kart world X
   * @param z   Kart world Z
   * @param yaw Kart yaw (radians)
   * @param dt  Frame delta time
   */
  emitBoostParticles(x: number, z: number, yaw: number, dt: number): void {
    const pScale = clientSettings.getParticleScale();
    if (pScale <= 0) return;

    // Emit particles per second while boosting, scaled by density setting
    const count = Math.max(1, Math.round(100 * dt * pScale));

    const behindX = -Math.cos(yaw);
    const behindZ = -Math.sin(yaw);

    // Emit from two exhaust points
    const rightX = -Math.sin(yaw);
    const rightZ = Math.cos(yaw);

    for (const side of [-0.3, 0.3]) {
      this.boostSystem.emit(
        x + behindX * 1.6 + rightX * side,
        0.45,
        z + behindZ * 1.6 + rightZ * side,
        behindX, 0.15, behindZ,
        Math.ceil(count / 2)
      );
    }
  }

  /**
   * Emit a burst of sparks when the kart hits a wall. Call once per collision.
   *
   * @param x       Collision world X
   * @param z       Collision world Z
   * @param normalX Wall normal X (pointing away from wall)
   * @param normalZ Wall normal Z
   */
  emitWallSparks(x: number, z: number, normalX: number, normalZ: number): void {
    const pScale = clientSettings.getParticleScale();
    if (pScale <= 0) return;

    // Sparks fly along the wall normal (away from wall) and upward
    this.sparkSystem.emit(
      x, 0.3, z,
      normalX, 0.6, normalZ,
      Math.max(1, Math.round(25 * pScale))
    );
  }

  /**
   * Emit a golden sparkle burst when an item box is collected. Call once per pickup.
   *
   * @param x Pickup world X
   * @param z Pickup world Z
   */
  emitPickupSparkle(x: number, z: number): void {
    const pScale = clientSettings.getParticleScale();
    if (pScale <= 0) return;

    // Burst upward and outward in all directions
    this.pickupSystem.emit(
      x, 1.8, z,
      0, 1.0, 0,
      Math.max(1, Math.round(30 * pScale))
    );
  }

  /**
   * Emit sparkles around a kart during a barrel roll.
   */
  emitBarrelRollSparkle(x: number, y: number, z: number, yaw: number): void {
    const rightX = -Math.sin(yaw);
    const rightZ = Math.cos(yaw);

    // Sparkles radiate outward from both sides
    this.pickupSystem.emit(
      x + rightX * 1.5, y + 0.5, z + rightZ * 1.5,
      rightX, 0.5, rightZ,
      3
    );
    this.pickupSystem.emit(
      x - rightX * 1.5, y + 0.5, z - rightZ * 1.5,
      -rightX, 0.5, -rightZ,
      3
    );
  }

  /**
   * Emit slipstream wind lines trailing from behind a kart.
   * Call every frame while the kart is in a slipstream.
   *
   * @param x   Kart world X
   * @param z   Kart world Z
   * @param yaw Kart yaw (radians)
   * @param dt  Frame delta time
   */
  emitSlipstreamLines(x: number, z: number, yaw: number, dt: number): void {
    const pScale = clientSettings.getParticleScale();
    if (pScale <= 0) return;

    const count = Math.max(1, Math.round(60 * dt * pScale));

    const behindX = -Math.cos(yaw);
    const behindZ = -Math.sin(yaw);

    // Emit from both sides slightly behind the kart
    const rightX = -Math.sin(yaw);
    const rightZ = Math.cos(yaw);

    for (const side of [-0.5, 0, 0.5]) {
      this.slipstreamSystem.emit(
        x + behindX * 2.5 + rightX * side,
        0.5,
        z + behindZ * 2.5 + rightZ * side,
        behindX, 0.1, behindZ,
        Math.ceil(count / 3)
      );
    }
  }

  // ────────────────────────────────────────────────────────────
  // Update & lifecycle
  // ────────────────────────────────────────────────────────────

  /** Advance all particle systems. Call once per frame. */
  update(dt: number): void {
    this.dustSystem.update(dt);
    this.boostSystem.update(dt);
    this.sparkSystem.update(dt);
    this.pickupSystem.update(dt);
    this.slipstreamSystem.update(dt);
  }

  /** Dispose all GPU resources. */
  dispose(): void {
    this.dustSystem.dispose();
    this.boostSystem.dispose();
    this.sparkSystem.dispose();
    this.pickupSystem.dispose();
    this.slipstreamSystem.dispose();
  }
}
